import { CombinedGraphQLErrors, ErrorLike, ServerError } from '@apollo/client'

/**
 * Represents a determinate status for a given request or query.
 * Can be used to return a concrete "status" for any given asynchronous request.
 */
export type QueryStatus = 'resolved' | 'rejected' | 'pending'
export type LazyQueryStatus = 'idle' | QueryStatus
export type MutationStatus = LazyQueryStatus

export const computeMutationStatus = ({
  error,
  loading,
  called,
}: {
  loading: boolean
  error: ErrorLike | undefined
  called: boolean
}) => {
  return loading ? 'pending' : error ? 'rejected' : called ? 'resolved' : 'idle'
}

export type QueryError = {
  message: string
  status: number
}

const getStatusCodeFromCustomExtensions = (extensions: Record<string, unknown> | undefined) => {
  if (extensions?.status) {
    const statusExtension: string = extensions.status as string
    return statusExtension
  }
  if (extensions?.response) {
    const responseExtension: { status?: string; statusCode?: string } = extensions.response as {
      status?: string
      statusCode?: string
    }
    if (responseExtension.status) {
      return responseExtension.status
    }
    if (responseExtension.statusCode) {
      return responseExtension.statusCode
    }
  }
  return
}

export const convertGraphqlErrorToQueryError = (
  error: ErrorLike | undefined | object | string
): QueryError[] => {
  if (CombinedGraphQLErrors.is(error)) {
    const discoveredStatuses: Array<number | string> = []
    error.errors.map(error => {
      const discoveredStatusCode = getStatusCodeFromCustomExtensions(error.extensions)
      if (discoveredStatusCode) discoveredStatuses.push(discoveredStatusCode)
    })
    const discoveredStatusCode = getStatusCodeFromCustomExtensions(error.extensions)
    if (discoveredStatusCode) discoveredStatuses.push(discoveredStatusCode)
    // Errors without a status extension still need to reach the UI, so fall back to a 500.
    if (discoveredStatuses.length === 0) discoveredStatuses.push(500)
    return discoveredStatuses.map(discoveredStatus => {
      return {
        message: error.message,
        status:
          typeof discoveredStatus === 'string'
            ? parseInt(discoveredStatus)
            : discoveredStatus
            ? discoveredStatus
            : 500,
      }
    })
  } else if (ServerError.is(error)) {
    return [
      {
        message: error.message,
        status: error.statusCode,
      },
    ]
  } else if (error) {
    return [
      {
        status: 500,
        message:
          typeof error === 'string'
            ? error
            : 'message' in error
            ? error.message
            : 'Uncategorized error',
      },
    ]
  } else {
    return []
  }
}
